import { useMutation } from '@apollo/client';
import { saveAs } from 'file-saver';
import { successNotification, errorNotification } from "components/request-result";
import {
    EXPORT_EXCEL_GRAPHIC_STAND,
    EXPORT_EXCEL_GRAPHIC_STORED_ALL,
    EXPORT_EXCEL_GRAPHIC_STORED_AVAILABLE
} from 'graphql/mutation/graphic-stand-gql';

const useMutationGraphicStandExportExcel = () => {
    const [ exportExcelGraphicStand, { loading: loadingMutationGraphicStandExportExcel } ] = useMutation(EXPORT_EXCEL_GRAPHIC_STAND);
    const [ exportExcelGraphicStoredAll, { loading: loadingMutationGraphicStoredAllExportExcel } ] = useMutation(EXPORT_EXCEL_GRAPHIC_STORED_ALL);
    const [ exportExcelGraphicStoredAvailable, { loading: loadingMutationGraphicStoredAvailableExportExcel } ] = useMutation(EXPORT_EXCEL_GRAPHIC_STORED_AVAILABLE);

    const downloadFile = async (fileUrl, fallbackFileName) => {
        try {
            const response = await fetch(fileUrl);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${ response.status }`);
            }

            const blob = await response.blob();
            const fileName = fileUrl.split('/').pop() || fallbackFileName;

            saveAs(blob, fileName);
            return { success: true };
        } catch (downloadError) {
            console.error('Download error:', downloadError);
            return { success: false, error: 'Error downloading the file:' + downloadError.message };
        }
    };

    const executeExport = async ({ mutation, mutationOptions, dataPath, fallbackFileName }) => {
        try {
            const { data } = await mutation(mutationOptions);
            const exportPayload = data?.[dataPath];
            const fileUrl = exportPayload?.file;

            if (!fileUrl) {
                return { success: false, error: 'No file data in server response' };
            }

            successNotification({
                title: exportPayload?.label,
                description: exportPayload?.message
            });

            return await downloadFile(fileUrl, fallbackFileName);
        } catch (error) {
            errorNotification(error);
            console.error('Export error:', error);
            return { success: false, error: error.message };
        }
    };

    const exportConfig = {
        stand: {
            mutation: exportExcelGraphicStand,
            dataPath: 'graphicListStandExportExel',
            fallbackFileName: (id) => `graphic-stand-export-${ id }.xlsx`,
            getMutationOptions: (id) => ({ variables: { id } })
        },
        storedAll: {
            mutation: exportExcelGraphicStoredAll,
            dataPath: 'graphicStoredAllExportExel',
            fallbackFileName: () => 'graphic-stored-all-export.xlsx'
        },
        storedAvailable: {
            mutation: exportExcelGraphicStoredAvailable,
            dataPath: 'graphicStoredAvailableExportExel',
            fallbackFileName: () => 'graphic-stored-available-export.xlsx'
        }
    };

    const runExport = (type, id) => {
        const config = exportConfig[type];

        return executeExport({
            mutation: config.mutation,
            mutationOptions: config.getMutationOptions ? config.getMutationOptions(id) : undefined,
            dataPath: config.dataPath,
            fallbackFileName: config.fallbackFileName(id)
        });
    };

    return {
        _setGraphicStandExportExcel: (id) => runExport('stand', id),
        loadingMutationGraphicStandExportExcel,
        _setGraphicStoredAllExportExcel: () => runExport('storedAll'),
        loadingMutationGraphicStoredAllExportExcel,
        _setGraphicStoredAvailableExportExcel: () => runExport('storedAvailable'),
        loadingMutationGraphicStoredAvailableExportExcel
    };
};

export default useMutationGraphicStandExportExcel;